import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet } from 'lucide-react';
import {
  GetPayrollTableExportSettings,
  UpdatePayrollTableExportSettings,
} from '../../../../api/Payroll/payrollReports';
import Alert from '../../../../components/ui/Alert';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { Spinner } from '../../../../components/ui/Spinner';
import { Switch } from '../../../../components/ui/Switch';
import { getApiErrorMessage } from '../../../../utils/apiErrors';

/**
 * "Allow bank details in payroll exports". Its own page, because the export runs for every company whatever its
 * compliance country, and its own HR-only endpoint rather than the CompanyRules round trip, whose POST sends the
 * whole view back and could silently revert this. A switch saves on its own, and a failed save puts it back, so what
 * is on screen is always what is stored.
 */
const PayrollExportsSettings: React.FC = () => {
  const { t } = useTranslation();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    GetPayrollTableExportSettings()
      .then((r) => {
        if (cancelled) return;
        if (r.payload) setAllowed(r.payload.allowBankDetails);
        else setLoadFailed(true);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const change = async (next: boolean) => {
    const previous = allowed;
    setAllowed(next);
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const result = await UpdatePayrollTableExportSettings({ allowBankDetails: next });
      if (result.errors?.length) throw result;
      setSaved(true);
    } catch (e) {
      setAllowed(previous);
      setError(getApiErrorMessage(e, t, t('settings.payrollExport.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      title={t('settings.payrollExport.title')}
      description={t('settings.payrollExport.description')}
      icon={FileSpreadsheet}
      accentColor="blue"
    >
      <div
        className="space-y-3 rounded-xl border border-border bg-card px-5 py-5"
        data-testid="payroll-export-privacy-panel"
      >
        {loadFailed ? (
          <p className="text-sm text-destructive" role="alert">{t('settings.payrollExport.loadFailed')}</p>
        ) : allowed === null ? (
          <Spinner size="md" />
        ) : (
          <>
            <Switch
              checked={allowed}
              onCheckedChange={change}
              disabled={saving}
              label={t('settings.payrollExport.bankDetailsLabel')}
              description={t('settings.payrollExport.bankDetailsHint')}
              data-testid="payroll-export-bank-details-switch"
            />
            {!allowed && (
              <Alert variant="warning" className="w-full" data-testid="payroll-export-bank-details-off">
                <p className="max-w-[70ch] text-sm">{t('settings.payrollExport.bankDetailsOffWarning')}</p>
              </Alert>
            )}
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            {saved && !error && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400" role="status" data-testid="payroll-export-setting-saved">
                {t('settings.payrollExport.saved')}
              </p>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default PayrollExportsSettings;
